/**
 * useCaseEdits — the operator's edits for ONE case, restored and persisted.
 *
 * Extracted from CaseReportBuilder so the UI harness drives the REAL hook rather
 * than a copy of it. A copy proves the copy works; that is how a page-select
 * crash reached the operator while the engine under it had 200 tests.
 *
 * The edits and the case they belong to are ONE piece of state. Held apart,
 * switching case leaves a window where the previous case's edits are still in
 * state while the new case id is already current — and the save writes one
 * patient's captions under another patient's id. Kept together that window
 * cannot exist, because edits whose `caseKey` is not the current case are, by
 * construction, not this case's edits.
 */
import { useEffect, useState } from 'react'
import { loadEdits, saveEdits, clearEdits } from './editStore.js'

/** Frozen so an un-owned set is referentially stable and memos do not churn. */
const EMPTY = Object.freeze({})

export function useCaseEdits(caseId, storage = undefined) {
  const caseKey = String(caseId ?? '').trim()
  const [state, setState] = useState({ caseKey: null, edits: {} })
  const [restored, setRestored] = useState(null)
  const [saveState, setSaveState] = useState(null)

  const edits = state.caseKey === caseKey ? state.edits : EMPTY

  // Restore on mount and whenever the case changes. Storage never throws; a
  // missing, corrupt or foreign entry simply yields an empty set.
  useEffect(() => {
    const got = loadEdits(caseKey, storage)
    setState({ caseKey, edits: got?.edits ?? {} })
    setRestored(got && Object.keys(got.edits).length ? got : null)
    setSaveState(null)
  }, [caseKey, storage])

  // Persist. Guarded on the edits actually belonging to this case, so the first
  // render after a case change — before the restore effect has run — cannot
  // write the outgoing case's edits under the incoming id.
  //
  // ⚠️ DO NOT REMOVE THIS LINE because "nothing breaks without it". Nothing
  // VISIBLY breaks: the bad write is followed a millisecond later by a save of
  // the now-empty state, which deletes it again, so localStorage looks correct
  // afterwards. It took five attempts to write a test that could see this. What
  // actually happens without the guard, observed:
  //
  //   [save-effect] caseKey=O3D-HARNESS-  state.caseKey=O3D-HARNESS-A  pages=1
  //   → "STILL CASE A" written to o3d.caseReport.edits.v1:O3D-HARNESS-
  //
  // Every keystroke while retyping a Case ID is one of these. A tab closed, or a
  // browser killed, inside that window leaves one patient's captions filed under
  // another patient's id. Pinned by "editing the Case ID never writes one case's
  // edits under another" in tools/ui-harness/e2e.mjs, which asserts on the
  // CONTENT of each write — the key alone cannot show it, because saveEdits
  // stamps every payload with the case it is writing to.
  useEffect(() => {
    if (state.caseKey !== caseKey) return
    setSaveState(saveEdits(caseKey, state.edits, storage))
  }, [state, caseKey, storage])

  const setEdits = (updater) => setState((s) => ({
    caseKey,
    edits: typeof updater === 'function' ? updater(s.caseKey === caseKey ? s.edits : {}) : updater,
  }))

  const clear = () => {
    clearEdits(caseKey, storage)
    setState({ caseKey, edits: {} })
    setRestored(null)
  }

  return { caseKey, edits, setEdits, restored, saveState, clear }
}
